import { z } from 'zod';
import { CurrencyCode } from './currency.js';
import { SCHEMA_ICON_PICKER_NAMES } from './schema-icon-picker.js';
export const jsonLogicSchema = z.union([
    z.boolean(),
    z.string(),
    z.number(),
    z.null(),
    z.record(z.string(), z.any()),
    z.array(z.any()),
]);
export const schemaMappingEnum = z.enum([
    'contacts_schema',
    'transactions_schema',
    'custom_schema',
    'combined_schema', // this is a combined schema that contains both contacts and transactions data
    'locations_schema',
    /**
     * This is a schema that contains customer fields for a business (e.g. number of dogs owned, etc.)
     */
    'customer_fields',
]);
export const ExposureEnum = {
    SEGMENTS: 'segments',
    MERGE_FIELDS: 'merge-fields',
    AGENT_TOOLS: 'agent-tools',
};
export const fieldExposureSchema = z.nativeEnum(ExposureEnum);
export const schemaIconSchema = z.object({
    type: z.literal('lucide'),
    value: z.enum(SCHEMA_ICON_PICKER_NAMES),
});
const MAX_DISPLAY_METADATA_FIELD_PATH_LENGTH = 200;
const MAX_SCHEMA_DISPLAY_LABEL_LENGTH = 60;
const normalizeDisplayMetadataFieldPath = (path) => path
    .trim()
    .replace(/^\$\.?/, '')
    .replace(/^\./, '');
const displayMetadataFieldPathSchema = z
    .string()
    .trim()
    .min(1)
    .max(MAX_DISPLAY_METADATA_FIELD_PATH_LENGTH)
    .refine(path => !path.startsWith('$') || path.startsWith('$.'), {
    message: 'Field path must use "$.field" when prefixed with "$"',
})
    .transform(normalizeDisplayMetadataFieldPath)
    .pipe(z
    .string()
    .min(1)
    .regex(/^[^.\s]+(?:\.[^.\s]+)*$/, 'Field path must use non-empty dot-separated segments'));
const reportingStatusSchema = z
    .object({
    path: displayMetadataFieldPathSchema,
})
    .strict();
const reportingEffectiveDateSchema = z
    .object({
    path: displayMetadataFieldPathSchema,
})
    .strict();
const reportingResourceLinkSchema = z.discriminatedUnion('source', [
    z
        .object({
        source: z.literal('field'),
        path: displayMetadataFieldPathSchema,
    })
        .strict(),
    z
        .object({
        source: z.literal('pattern'),
        pattern: z
            .string()
            .trim()
            .min(1)
            .max(2048)
            .transform(pattern => pattern.replace(/{([^{}]+)}/g, (_placeholder, path) => `{${normalizeDisplayMetadataFieldPath(path)}}`)),
    })
        .strict(),
]);
export const schemaReportingMetadataSchema = z
    .object({
    status: reportingStatusSchema.optional(),
    effectiveDate: reportingEffectiveDateSchema.optional(),
    resourceLink: reportingResourceLinkSchema.optional(),
})
    .strict();
const schemaDisplayLabelSchema = z
    .string()
    .trim()
    .min(1)
    .max(MAX_SCHEMA_DISPLAY_LABEL_LENGTH)
    .describe('Human-readable schema name shown in place of the schema name, e.g. "Job"');
export const schemaDisplayMetadataSchema = z
    .object({
    label: schemaDisplayLabelSchema.optional(),
    icon: schemaIconSchema.optional(),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a 6-digit hex like #22c55e')
        .optional(),
    reporting: schemaReportingMetadataSchema.optional(),
})
    .strict();
export const schemaReportingMetadataPatchSchema = z
    .object({
    status: reportingStatusSchema.nullable().optional(),
    effectiveDate: reportingEffectiveDateSchema.nullable().optional(),
    resourceLink: reportingResourceLinkSchema.nullable().optional(),
})
    .strict();
export const schemaDisplayMetadataPatchSchema = z
    .object({
    label: schemaDisplayLabelSchema.nullable().optional(),
    icon: schemaIconSchema.nullable().optional(),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a 6-digit hex like #22c55e')
        .nullable()
        .optional(),
    reporting: schemaReportingMetadataPatchSchema.nullable().optional(),
})
    .strict();
function exhaustiveKeys() {
    return (keys) => keys;
}
export const SCHEMA_DISPLAY_METADATA_LEAF_KEYS = exhaustiveKeys()(['label', 'icon', 'color']);
export const SCHEMA_REPORTING_METADATA_LEAF_KEYS = exhaustiveKeys()([
    'status',
    'effectiveDate',
    'resourceLink',
]);
/**
 * This enum is used by JSON schema to define the type of a field
 */
export var JSONSchemaType;
(function (JSONSchemaType) {
    JSONSchemaType["String"] = "string";
    JSONSchemaType["Number"] = "number";
    JSONSchemaType["Integer"] = "integer";
    JSONSchemaType["Boolean"] = "boolean";
    JSONSchemaType["Array"] = "array";
    /**
     * Dates are strings but have a specific format
     * @see PartnerUserService.getTypeFromJsonSchema
     */
    JSONSchemaType["Date"] = "date";
    JSONSchemaType["DateTime"] = "date-time";
    /**
     * This is a schema that contains a reference to a Reach resource
     */
    JSONSchemaType["ReachResource"] = "reach-resource";
    /**
     * Unsupported types (for now...)
     */
    // Object = 'object',
    // Null = 'null',
})(JSONSchemaType || (JSONSchemaType = {}));
export const contactStatusSchema = z.object({
    field: z.string(),
    activeValue: z.string(),
    deactivatedValue: z.string(),
});
/**
 * A named sender profile. Each profile is keyed by `profileId` and labeled with a human-readable
 * `label` (e.g. "Default Sender", "Doctor"); it maps that label to a pair of dot-notation field
 * paths on the hydrated partner resource that resolve to the sender's display name and email.
 *
 * Field paths support nested traversal:
 *   - "fieldName"           → resource.fieldName
 *   - "fieldName.subField"  → resource.fieldName.subField (hydrated reference)
 */
export const senderProfileSchema = z.object({
    profileId: z.string().uuid(),
    label: z.string().min(1),
    nameFieldPath: z.string().min(1),
    emailFieldPath: z.string().min(1),
});
export const contactSourceSchema = z
    .object({
    type: z.literal(schemaMappingEnum.enum.contacts_schema),
    schemaId: z
        .string()
        .uuid('schemaID must be a UUID referencing a schema definition of type contacts_schema'),
    email: z.string().optional(),
    phone: z.string().optional(),
    userId: z.string().min(1, 'User ID field is required'),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    fullName: z.string().optional(),
    location: z.string().optional(),
    status: contactStatusSchema.optional(),
    createdDate: z.string().optional(),
    emailOptOut: z.string().optional(),
    emailOptIn: z.string().optional(),
    emailOptOutTimestamp: z.string().optional(),
    smsOptOut: z.string().optional(),
    smsOptIn: z.string().optional(),
    urlPattern: z.string().optional(),
    smsOptOutTimestamp: z.string().optional(),
    /** Partner resource field path for point-of-contact display name (when POC sender is enabled). */
    pointOfContactName: z.string().optional(),
    /** Partner resource field path for point-of-contact email (when POC sender is enabled). */
    pointOfContactEmail: z.string().optional(),
    /**
     * Named sender profiles available for selection on automations/templates. When multiple
     * profiles are defined, the UI renders a chooser so the partner/tenant can pick which one
     * to use per automation. Each profile is keyed by its `profileId` and labeled by its `label`.
     */
    senderProfiles: z.array(senderProfileSchema).min(1).optional(),
    /** Partner resource field path for HIPAA consent (opt-in). Boolean value at this path indicates the contact has given HIPAA consent. */
    hipaaConsent: z.string().optional(),
})
    .refine(data => {
    // Either (firstName AND lastName) OR fullName must be provided
    const hasFirstAndLast = data.firstName && data.lastName;
    const hasFullName = data.fullName;
    return hasFirstAndLast || hasFullName;
}, {
    message: 'Either both first name and last name, or full name must be provided',
    path: ['firstName'], // This will show the error on the firstName field
})
    .refine(data => {
    // Either email OR phone (or both) must be provided
    const hasEmail = data.email && data.email.trim().length > 0;
    const hasPhone = data.phone && data.phone.trim().length > 0;
    return hasEmail || hasPhone;
}, {
    message: 'Either email or phone (or both) must be provided',
    path: ['email'], // This will show the error on the email field
});
export const locationSourceSchema = z.object({
    type: z.literal(schemaMappingEnum.enum.locations_schema),
    schemaId: z
        .string()
        .uuid('schemaID must be a UUID referencing a schema definition of type locations_schema'),
    id_field: z.string().min(1, 'ID field is required'),
    name: z.string().optional(),
    address_line_1: z.string().optional(),
    address_line_2: z.string().optional(),
    address_line_3: z.string().optional(),
    locality: z.string().optional(),
    district: z.string().optional(),
    postal_code: z.string().optional(),
    country_code: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    website: z.string().optional(),
    urlPattern: z.string().optional(),
});
export const contactSchemaMappingSchema = z.object({
    primarySource: contactSourceSchema,
    additionalSources: z.array(contactSourceSchema).optional(),
});
export const billableEventFilterConditionSchema = z.object({
    path: z
        .string()
        .describe('Dot notation path to the value, e.g., "primaryServiceType.serviceType.isVisit"'),
    operator: z.enum(['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'exists', 'not_exists']),
    value: z.any().describe('The value to compare against. Not used for "exists" and "not_exists"'),
});
export const billableEventFilterSchema = z.object({
    logicalOperator: z.enum(['AND', 'OR']).default('AND'),
    conditions: z.array(billableEventFilterConditionSchema),
});
export const transactionAmountSchema = z.object({
    field: z.string().min(1, 'Amount field is required'), // the field that contains the amount
    currencyField: z.string().optional(), // the field that contains the currency
    currency: z.nativeEnum(CurrencyCode).optional(), // fallback when the currency field is unset or empty; USD if neither
});
export const transactionDatesSchema = z.object({
    createdDate: z.string().optional(),
    paidDate: z.string().optional(),
    dueDate: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
});
export const transactionSchema = z.object({
    type: z.literal(schemaMappingEnum.enum.transactions_schema),
    eventType: z.string(),
    schemaId: z
        .string()
        .uuid('schemaID must be a UUID referencing a schema definition of type transactions_schema'),
    id_field: z.string().min(1, 'ID field is required'),
    userId: z.string().min(1, 'User ID field is required'),
    amount: transactionAmountSchema,
    dates: transactionDatesSchema,
    locationId: z.string().optional().describe('Location ID field'),
    metadata: z.array(z.string()).optional(),
    filter: jsonLogicSchema
        .optional()
        .describe('JSONLogic rule to conditionally create billable events from resources. See: https://jsonlogic.com/'),
    urlPattern: z.string().optional(),
});
export const customEventSchema = z.object({
    type: z.literal(schemaMappingEnum.enum.custom_schema),
    id_field: z.string().min(1, 'ID field is required'),
    date: z.string().min(1, 'Date field must not be empty').optional(),
    userId: z.string().min(1, 'User ID field is required'),
    metadata: z.array(z.string()).optional(),
    schemaId: z.string().min(1, 'Schema ID is required'),
});
export const partnerSchemaMappingsSchema = z.object({
    type: z.literal(schemaMappingEnum.enum.combined_schema),
    contactsSchema: contactSchemaMappingSchema.optional(),
    transactionsSchema: z.array(transactionSchema).optional(),
    customSchema: z.array(customEventSchema).optional(),
    locationsSchema: z.array(locationSourceSchema).optional(),
});
export const SCHEMA_LOOKUP_TOOL_CONFLICT_CODE = 'SCHEMA_LOOKUP_TOOL_CONFLICT';
const schemaLookupToolConflictBaseSchema = z.object({
    toolDefinitionId: z.string().uuid(),
    toolName: z.string().min(1),
});
const schemaLookupToolMappingConflictSchema = schemaLookupToolConflictBaseSchema.extend({
    schemaDefinitionId: z.string().uuid(),
    reason: z.enum(['schema_removed', 'user_id_invalid']),
});
const invalidSchemaLookupToolConfigurationConflictSchema = schemaLookupToolConflictBaseSchema.extend({
    schemaDefinitionId: z.string().uuid().optional(),
    reason: z.literal('invalid_configuration'),
});
export const schemaLookupToolConflictSchema = z.union([
    schemaLookupToolMappingConflictSchema,
    invalidSchemaLookupToolConfigurationConflictSchema,
]);
export const schemaLookupToolConflictPayloadSchema = z.object({
    code: z.literal(SCHEMA_LOOKUP_TOOL_CONFLICT_CODE),
    message: z.string(),
    conflicts: z.array(schemaLookupToolConflictSchema),
});
export const schemaLookupToolConflictResponseDataSchema = z.object({
    error: schemaLookupToolConflictPayloadSchema,
});
//# sourceMappingURL=schema-mappings.js.map